import { effect, inject, Injectable, linkedSignal } from '@angular/core';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';

interface PanelState {
  foldersOpen: boolean;
  openedFormId: string | null;
}
const empty = (): PanelState => ({ foldersOpen: false, openedFormId: null });

/**
 * The Forms panel's navigation (Folders mode, the open form), kept per space and account so a reload or a remount of the panel
 * comes back where it was. Ported from `SlidesPanelStateService` (`slides-panel-state.service.ts`) minus its per-deck canvas view,
 * which a form's record preview does not have. Navigation only: it never selects or filters anything in the main view, and the form
 * content stays in the space-scoped `FormsStore`. Provided on the panel, so a space switch re-reads the other space's entry.
 */
@Injectable()
export class FormsPanelStateService {
  private readonly context = inject(SpaceContextStore);
  private readonly user = inject(CurrentUserStore);
  /** Follows the space/account key synchronously, so a switch never shows (or writes) the other space's entry. */
  public readonly state = linkedSignal<string, PanelState>({ source: () => this.storageKey(), computation: (key) => this.read(key) });

  public constructor() {
    effect(() => {
      const key = this.storageKey();
      const state = this.state();
      try {
        localStorage.setItem(key, JSON.stringify(state));
      } catch {
        /* Storage may be unavailable. */
      }
    });
  }

  private storageKey(): string {
    return `forms-fourth-panel.${this.context.contextId() ?? 'personal'}.${this.user.accountId() ?? 'anonymous'}`;
  }

  private read(key: string): PanelState {
    try {
      const value: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
      if (!value || typeof value !== 'object' || !('openedFormId' in value)) return empty();
      return { foldersOpen: 'foldersOpen' in value && value.foldersOpen === true, openedFormId: typeof value.openedFormId === 'string' ? value.openedFormId : null };
    } catch {
      return empty();
    }
  }

  public showFolders(foldersOpen: boolean): void {
    this.state.update((state) => ({ ...state, foldersOpen, ...(foldersOpen ? { openedFormId: null } : {}) }));
  }

  public open(id: string | null): void {
    this.state.update((state) => (state.openedFormId === id && (!id || !state.foldersOpen) ? state : { ...state, openedFormId: id, ...(id ? { foldersOpen: false } : {}) }));
  }
}
